import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { I18nContext, type I18nValue } from './context';
import { LANG_STORAGE_KEY, isLang, resolve, type Lang, type MessageId, type Vars } from './messages';

/**
 * طبقة اللغة: النصّ وحده يتغيّر — الاتجاه يبقى RTL والقوائم في مواضعها.
 * «لو كانت يمين تبقى يمين»: لا نلمس dir ولا نقلب التخطيط، ولا نستبدل
 * الخصائص المنطقية. ما يُحدَّث هو سمة lang على <html> (للخط وقارئ الشاشة).
 */
function initialLang(): Lang {
  try {
    const stored = localStorage.getItem(LANG_STORAGE_KEY);
    if (isLang(stored)) return stored;
  } catch {
    /* التخزين محجوب (نافذة خاصة أو سياسة الجهة) — نُكمل بالافتراضي */
  }
  const attr = document.documentElement.getAttribute('lang');
  return isLang(attr) ? attr : 'ar';
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initialLang);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('lang', lang);
    // الاتجاه لا يُلمس: يبقى rtl كما هو في index.html
    // عنوان التبويب نصٌّ خام لا يحتاج عازل الاتجاه — تُنزع علامتاه هنا
    document.title = resolve(lang, 'app.title').replace(/[\u2066\u2069]/g, '');
  }, [lang]);

  /*
    الإنجليزيةُ لكل ما على الشاشة (23 سبتمبر 2026): ما لا يمرّ بـ`t()` — آلافُ النصوص في الشاشات
    ورسائلُ الخادم وما يكتبه الناس — تترجمه طبقةٌ تراقب الصفحة (`live-translate`). والفهرسُ (~1 م.ب)
    يُحمَّل عند اختيار الإنجليزية وحدها: قارئُ العربية لا يدفع ثمنه.
  */
  useEffect(() => {
    let cancelled = false;
    if (lang === 'en') {
      // الفهرسُ أصلٌ ثابت (`?url`) يُجلب JSON لا حزمةُ JS بـ1.8 م.ب تُصرَّف — كان أكبرَ حزمةٍ في البناء (22 أكتوبر 2026)
      void Promise.all([
        import('./live-translate'),
        import('./catalog/en.json?url').then((m) => fetch(m.default).then((r) => { if (!r.ok) throw new Error(`catalog ${r.status}`); return r.json() as Promise<Record<string, string>>; })),
      ]).then(([lt, cat]) => {
        if (!cancelled) lt.start(cat);
      }).catch((e: unknown) => { console.warn('[rafrafa] تعذّر تحميل فهرس الإنجليزية — تبقى الواجهةُ بالعربية:', e); });
    } else {
      void import('./live-translate').then((lt) => lt.stop());
    }
    return () => { cancelled = true; };
  }, [lang]);

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    try {
      localStorage.setItem(LANG_STORAGE_KEY, next);
    } catch {
      /* لا تخزين — الاختيار يسري لهذه الجلسة فقط */
    }
  }, []);

  const value = useMemo<I18nValue>(
    () => ({ lang, setLang, t: (id: MessageId, vars?: Vars) => resolve(lang, id, vars) }),
    [lang, setLang],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
